/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "@app/axios/axiosIns";

const pickFilenameFromContentDisposition = (value?: string): string | undefined => {
  if (!value) return undefined;

  const match =
    value.match(/filename\*=\s*UTF-8''([^;]+)/i) ||
    value.match(/filename\s*=\s*"([^"]+)"/i) ||
    value.match(/filename\s*=\s*([^;]+)/i);

  const raw = match?.[1]?.trim();
  if (!raw) return undefined;

  try {
    return decodeURIComponent(raw.replace(/^["']|["']$/g, ""));
  } catch {
    return raw.replace(/^["']|["']$/g, "");
  }
};

export const getAllChannelPartners = createAsyncThunk(
  "channelPartner/all",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/admin/channel-partner", {
        params,
      });
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const exportChannelPartners = createAsyncThunk(
  "channelPartner/export/all",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/admin/channel-partner/export/all", {
        responseType: "blob",
      });

      const headers = response?.headers || {};
      const contentDisposition: string | undefined =
        headers["content-disposition"] || headers["Content-Disposition"];

      return {
        blob: response?.data as Blob,
        filename:
          pickFilenameFromContentDisposition(contentDisposition) ||
          "channel_partners_export.csv",
        contentType:
          (headers["content-type"] || headers["Content-Type"]) as string | undefined,
      };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  }
);

export const deleteChannelPartner = createAsyncThunk(
  "channelPartner/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/admin/channel-partner/${id}`);
      return { id, data: response?.data };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

interface ChannelPartnerState {
  isLoading: boolean;
  formLoader: boolean;
  allChannelPartners: any;
  error: any;
}

const initialState: ChannelPartnerState = {
  isLoading: false,
  formLoader: false,
  allChannelPartners: [],
  error: null,
};

export const channelPartnerSlice = createSlice({
  name: "channelPartner",
  initialState,
  reducers: { resetState: () => initialState },
  extraReducers: (builder) => {
    builder
      .addCase(getAllChannelPartners.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllChannelPartners.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allChannelPartners = action.payload;
      })
      .addCase(getAllChannelPartners.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(deleteChannelPartner.pending, (state) => {
        state.formLoader = true;
      })
      .addCase(deleteChannelPartner.fulfilled, (state) => {
        state.formLoader = false;
      })
      .addCase(deleteChannelPartner.rejected, (state) => {
        state.formLoader = false;
      });
  },
});

export const { resetState } = channelPartnerSlice.actions;
export default channelPartnerSlice.reducer;

